import { computed, nextTick, ref, watch, type ComputedRef, type Ref } from 'vue'
import { executePersonalReview } from '../../../integrations/personalReview'
import {
  actionOutputPorts,
  decodeAudioAction,
  evaluateCondition,
  indexGraph,
  nextNodeId,
  readNodeString as graphStr,
} from '../../../../../../shared/flow-core/src/execution'
import type { DeckCardInput, DeckElementInteraction } from '../../../deck/types'
import type { DeckTemplateSettings } from '../../../deck/templateSettings'
import { postDeckPlayerFlowProgress } from '../../api/deckPlayerApi'
import type { DeckPlayerModel, DeckPlayerShortcodeProps } from '../../types'
import type { FlowGraphNodeV1, FlowGraphV1 } from '../flowGraph'
import {
  countPrimaryCheckboxes,
  initialCheckboxValues,
  pullTransition,
  selectPullPool,
  toggleCheckboxes,
} from './cardActions'
import {
  configuredMediaType,
  detectVideoOverride,
  linkedMaterial,
  mediaLog,
  type MediaDisplayMode,
  type MediaMaterial,
  type MediaType,
} from './media'
import { playCardAudio } from './playAudio'
import {
  applyMathOp,
  EMPTY_REGISTERS,
  readGetterSource,
  readSetValueInput,
  type FlowContext,
} from './registers'
import { resetForDeckChange, resetForStart, type FlowSessionState } from './sessionState'

/** The props of DeckPlayerShell the flowchart runtime reads. */
export type FlowRunnerProps = {
  deckId: string
  model: DeckPlayerModel
  cards: DeckCardInput[]
  deckGroups?: Array<{ id?: string; title: string; cardIds: string[] }>
  flowGraph?: FlowGraphV1 | null
  forceComplete?: boolean
  keepCardOnFlowEnd?: boolean
  suppressProgressWrites?: boolean
  deckTemplate?: DeckTemplateSettings
  initialValues?: Record<string, boolean[]>
  initialFlowPhaseId?: string | null
  playerProps?: DeckPlayerShortcodeProps
  isLoggedIn?: boolean
  restRoot?: string
  nonce?: string
  manualStep?: boolean
  stepSignal?: number
  debugStepDelay?: number
  inEditor?: boolean
  mediaMaterials?: MediaMaterial[]
  /** Course data for phase getters (stored answers of other levels). */
  flowContext?: FlowContext | null
  onMediaPause?: (
    info: { displayMode: string; mediaType: string; mediaName: string; mediaId: string },
    resume: () => void,
  ) => void
}

export type FlowRunnerEmit = {
  (e: 'card-removed', payload: { cardId: string }): void
  (
    e: 'flowchart-step-changed',
    payload: { nodeId: string | null; needsUserInteraction?: boolean },
  ): void
  (e: 'phase-interlude', payload: { deckId: string; phaseId: string | null }): void
  (e: 'show-media', payload: { mediaType: MediaType; mediaName: string; mediaId: string }): void
}

export type FlowRunnerDeps = {
  props: FlowRunnerProps
  emit: FlowRunnerEmit
  state: FlowSessionState
  graph: ComputedRef<FlowGraphV1 | null>
  deckCardIds: ComputedRef<string[]>
  playerEl: Ref<HTMLElement | null>
  /** The card as displayed (checkbox state applied); play-audio verifies against it. */
  displayedCard: () => DeckCardInput | null
  cardChangeAnimation: () => string
  fireConfetti: () => Promise<void>
  /** Called whenever a blocking media node is shown (resets now-playing state). */
  onMediaShown: () => void
}

const hasOwn = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key)

/** Executes a deck's flow graph: one node at a time, pausing for the learner. */
export function useFlowchartRunner(deps: FlowRunnerDeps) {
  const { props, emit, state } = deps

  // Debug controls (floating debug panel)
  /** When true, advancing stores continuations instead of executing immediately. */
  const dbgPaused = ref(false)
  /** Delay between auto-advanced nodes in ms (min 25). Persists across pause/resume. */
  const dbgStepDelayMs = ref(25)

  const index = computed(() => indexGraph(deps.graph.value))
  const nodeById = computed(() => index.value.nodes)
  const edgesByNodeId = computed(() => index.value.outgoing)

  const nextOf = (fromNodeId: string, preferPorts: string[], strict = false) =>
    nextNodeId(edgesByNodeId.value.get(fromNodeId) ?? [], preferPorts, strict)

  const countCheckboxes = (cardId: string) =>
    countPrimaryCheckboxes(props.cards.find((c) => c.id === cardId))

  /** Persists the active phase and all tracked checkbox states. Fire-and-forget. */
  const saveFlowProgress = () => {
    if (!props.isLoggedIn) return
    if (props.suppressProgressWrites) return
    const phaseId = state.activePhaseId.value
    if (!phaseId) return
    const checkboxStates: Record<string, boolean[]> = {}
    for (const [cardId, values] of state.cardCheckboxValues.value) checkboxStates[cardId] = values
    void postDeckPlayerFlowProgress({
      deckId: props.deckId,
      model: props.model,
      phaseId,
      checkboxStates,
      restRoot: props.restRoot,
      nonce: props.nonce,
    }).catch(() => {
      /* non-fatal */
    })
  }

  const initPools = () => {
    const pools: Record<string, string[]> = { __all__: deps.deckCardIds.value.slice() }
    for (const group of props.deckGroups ?? []) {
      const id = typeof group?.id === 'string' ? group.id.trim() : ''
      if (id) pools[id] = Array.isArray(group.cardIds) ? group.cardIds.slice() : []
    }
    state.pools.value = pools
  }

  const cardIdsForGroup = (groupId: string | null): string[] => {
    if (!groupId || groupId === '__all__') return deps.deckCardIds.value.slice()
    const group = (props.deckGroups ?? []).find((item) => item.id === groupId)
    return Array.isArray(group?.cardIds) ? group.cardIds.slice() : []
  }

  /** Group a node leads into (breadth-first), for actions placed before their group gate. */
  const findNextGroupId = (nodeId: string): string | null => {
    const visited = new Set<string>([nodeId])
    const queue = (edgesByNodeId.value.get(nodeId) ?? []).map((edge) => edge.toNodeId)
    while (queue.length > 0) {
      const nextId = queue.shift()!
      if (visited.has(nextId)) continue
      visited.add(nextId)
      const node = nodeById.value.get(nextId)
      if (!node) continue
      if (node.type === 'group') return graphStr(node.data, 'groupId') ?? null
      for (const edge of edgesByNodeId.value.get(nextId) ?? []) {
        if (!visited.has(edge.toNodeId)) queue.push(edge.toNodeId)
      }
    }
    return null
  }

  /** Runs `fn` after `ms` unless the flow was restarted meanwhile. */
  const later = (ms: number, fn: () => void) => {
    const epoch = state.epoch.value
    window.setTimeout(() => {
      if (state.epoch.value !== epoch) return
      fn()
    }, ms)
  }

  /** Moves the cursor to `nodeId` and executes it from a fresh call depth. */
  const goTo = (nodeId: string) => {
    state.cursorNodeId.value = nodeId
    exec(nodeId, 0)
  }

  /**
   * Continues with the next node, honouring manual stepping, debug pause and the
   * per-node step delay. Deep synchronous chains are broken with a 0 ms timeout:
   * looping flows would otherwise hit the depth-300 guard before reaching their
   * get-value nodes, leaving registers stuck.
   */
  const advance = (nextId: string, depth: number) => {
    if (props.manualStep || dbgPaused.value) {
      state.manualContinuation.value = () => goTo(nextId)
      return
    }
    const delay = dbgStepDelayMs.value > 0 ? dbgStepDelayMs.value : (props.debugStepDelay ?? 0)
    if (delay > 0) {
      // Move the highlight first, then wait; a pause during the wait keeps the continuation.
      state.cursorNodeId.value = nextId
      state.manualContinuation.value = () => exec(nextId, 0)
      later(delay, () => {
        state.manualContinuation.value = null
        exec(nextId, 0)
      })
      return
    }
    state.cursorNodeId.value = nextId
    if (depth >= 100) {
      later(0, () => exec(nextId, 0))
      return
    }
    exec(nextId, depth + 1)
  }

  /** Follows the node's `next` port, if connected. */
  const advanceNext = (nodeId: string, depth: number) => {
    const next = nextOf(nodeId, ['next'])
    if (next) advance(next, depth)
  }

  /** Delays and checkbox actions wait `ms` before continuing (none in manual mode). */
  const advanceAfter = (nodeId: string, ms: number, depth: number) => {
    const next = nextOf(nodeId, ['next'])
    if (!next) return
    if (props.manualStep || ms === 0) {
      advance(next, depth)
      return
    }
    later(ms, () => goTo(next))
  }

  /**
   * Phase checkpoints pause the flow with an interlude (except during a start's
   * synchronous pass). Without an outgoing edge, Continue restarts the phase.
   */
  const enterInterlude = (nodeId: string, phaseId: string | null, celebrate: boolean) => {
    const next = nextOf(nodeId, ['next'])
    state.phaseInterlude.value = true
    const checkpointValues = new Map(state.cardCheckboxValues.value)
    state.phaseResume.value = next
      ? () => {
          state.phaseInterlude.value = false
          state.phaseResume.value = null
          goTo(next)
        }
      : () => {
          // At 0 ms speed start() runs synchronously until the first real timer, so
          // registers and the active group may already be set again by the time it
          // returns. Only checkbox values are restored; restoring the rest would
          // overwrite values set during that synchronous run.
          start(phaseId ?? undefined)
          state.cardCheckboxValues.value = new Map(checkpointValues)
        }
    emit('phase-interlude', { deckId: props.deckId, phaseId })
    if (celebrate) void deps.fireConfetti()
  }

  // Node handlers
  type Handler = (node: FlowGraphNodeV1, depth: number) => void

  const structureHandlers: Record<string, Handler> = {
    end: () => {
      state.isInitializing.value = false
      state.complete.value = true
    },
    start: (node, depth) => advanceNext(node.id, depth),
    'macro-start': (node, depth) => {
      // Follow the output port of the calling frame (defaults to out-0).
      const frame = state.callStack.value[state.callStack.value.length - 1]
      const next = nextOf(node.id, [frame?.entryPort ?? 'out-0', 'out-0', 'next'])
      if (next) advance(next, depth)
    },
    'macro-call': (node, depth) => {
      const macroRef = graphStr(node.data, 'macroRef')
      const macroStart = macroRef
        ? [...nodeById.value.values()].find(
            (n) => n.type === 'macro-start' && graphStr(n.data, 'macroName') === macroRef,
          )
        : undefined
      if (!macroStart) {
        // No (resolvable) macro: skip and follow the first output.
        const next = nextOf(node.id, ['out-0', 'next'])
        if (next) advance(next, depth)
        return
      }
      state.callStack.value.push({ callNodeId: node.id, entryPort: 'out-0' })
      state.cursorNodeId.value = macroStart.id
      exec(macroStart.id, depth + 1)
    },
    'macro-end': (node, depth) => {
      if (state.callStack.value.length === 0) {
        state.complete.value = true // no caller: treat as flow end
        return
      }
      const frame = state.callStack.value.pop()!
      // The macro-end input that was reached (in-N) selects the caller's output out-N.
      const incoming = [...edgesByNodeId.value.values()].flat().find((e) => e.toNodeId === node.id)
      const outPort = `out-${incoming?.toPort?.replace('in-', '') ?? '0'}`
      const outputCount = typeof node.data?.outputCount === 'number' ? node.data.outputCount : 1
      const ports = [outPort, ...Array.from({ length: outputCount }, (_, i) => `out-${i}`), 'next']
      const next = nextOf(frame.callNodeId, ports)
      if (next) advance(next, depth)
    },
    phase: (node, depth) => {
      const phaseId = graphStr(node.data, 'phaseId')
      if (phaseId) {
        const wasInPhase = !!state.activePhaseId.value
        state.activePhaseId.value = phaseId
        state.registers.value = EMPTY_REGISTERS()
        saveFlowProgress()
        if (!state.isInitializing.value) {
          enterInterlude(node.id, phaseId, wasInPhase)
          return
        }
      }
      advanceNext(node.id, depth)
    },
    group: (node, depth) => {
      const groupId = graphStr(node.data, 'groupId')
      if (groupId) state.activeGroupId.value = groupId // '__all__' = every deck card
      advanceNext(node.id, depth)
    },
    condition: (node, depth) => {
      const result = evaluateCondition(node.data, state.registers.value)
      const next = nextOf(node.id, result ? ['true'] : ['else', 'next'], true)
      if (next) advance(next, depth)
    },
  }

  const pullNextCard: Handler = (node, depth) => {
    const { poolId, pool } = selectPullPool(
      state.pools.value,
      state.activeGroupId.value,
      props.keepCardOnFlowEnd === true,
      deps.deckCardIds.value.length,
    )
    if (pool.length === 0) {
      // Pool empty: clear the card (it was marked done when pulled) and continue.
      state.currentCardId.value = null
      advanceNext(node.id, depth)
      return
    }
    const animation = graphStr(node.data, 'pullAnimation')
    if (animation === 'confetti') void deps.fireConfetti()
    state.cardTransition.value = pullTransition(animation, [
      props.playerProps?.successCardChangeAnimation,
      props.playerProps?.cardChangeAnimation,
      props.deckTemplate?.successCardChangeAnimation,
      props.deckTemplate?.cardChangeAnimation,
    ])

    const cardId = pool[0]!
    const backFirst =
      graphStr(node.data, 'actionType') === 'pull-next-card-back-first' ||
      graphStr(node.data, 'pullDirection') === 'back'
    state.pools.value = { ...state.pools.value, [poolId!]: pool.slice(1) }
    state.doneCardIds.value = new Set([...state.doneCardIds.value, cardId]) // done when pulled
    emit('card-removed', { cardId })
    // Set the face before mounting the card to avoid a front-face flash.
    state.showBackFirst.value = backFirst
    state.currentCardId.value = cardId

    if (props.manualStep) {
      // The card stays visible; the next step continues the graph.
      state.manualContinuation.value = () => {
        const next = nextOf(node.id, ['next'])
        if (next) goTo(next)
      }
      return
    }
    advanceNext(node.id, depth)
  }

  const showMedia: Handler = (node, depth) => {
    state.isInitializing.value = false
    const actionType = graphStr(node.data, 'actionType') ?? ''
    const configured = configuredMediaType(actionType, graphStr(node.data, 'mediaType'))
    const displayMode = (graphStr(node.data, 'displayMode') ?? 'standalone') as MediaDisplayMode
    const mediaName =
      typeof node.label === 'string' && node.label.trim() ? node.label.trim() : node.id
    const mediaId = node.id
    const linked = linkedMaterial(props.mediaMaterials, mediaId, mediaName, configured)
    const mediaType = detectVideoOverride(configured, linked?.url)

    if (displayMode === 'panel') {
      // Opens the host's side panel; the flow continues immediately.
      mediaLog('flow reached panel media node', { mediaType, mediaId, mediaName })
      emit('show-media', { mediaType, mediaName, mediaId })
      advanceNext(node.id, depth)
      return
    }

    // Standalone, modal and split pause the flow until the media is dismissed.
    deps.onMediaShown()
    state.showingMedia.value = {
      mediaType,
      displayMode,
      mediaName,
      mediaId,
      mediaTypeRight: graphStr(node.data, 'mediaTypeRight') as MediaType | undefined,
      splitSwap: node.data?.splitSwap === true,
    }
    const next = nextOf(node.id, ['next'])
    state.showMediaResume.value = () => {
      state.showingMedia.value = null
      state.showMediaResume.value = null
      if (next) goTo(next)
    }
    // Images and PDFs may continue at once; required audio/video announce the host's
    // Continue button only after playback ended.
    if (displayMode === 'standalone' || displayMode === 'split') {
      const waitForPlayback =
        (mediaType === 'video' || mediaType === 'audio') && linked?.completionRequired === true
      mediaLog('flow reached media node', {
        mediaType,
        mediaId,
        displayMode,
        completionRequired: linked?.completionRequired,
        legacySkippable: linked?.skippable,
        waitForPlayback,
        hasHostCallback: Boolean(props.onMediaPause),
      })
      if (!waitForPlayback) {
        mediaLog('requesting immediate Continue', { mediaType, mediaId })
        props.onMediaPause?.({ displayMode, mediaType, mediaName, mediaId }, dismissShowMedia)
      }
    }
  }

  const tickCheckboxes: Handler = (node, depth) => {
    const cardId = state.currentCardId.value
    if (cardId) {
      const n =
        typeof node.data?.checkboxCount === 'number' && node.data.checkboxCount >= 1
          ? Math.floor(node.data.checkboxCount)
          : 1
      const mode = graphStr(node.data, 'actionType') === 'click-checkbox' ? 'click' : 'unclick'
      const updated = toggleCheckboxes(
        state.cardCheckboxValues.value.get(cardId) ?? [],
        countCheckboxes(cardId),
        n,
        mode,
      )
      state.cardCheckboxValues.value = new Map(state.cardCheckboxValues.value).set(cardId, updated)
    }
    const ms =
      typeof node.data?.checkboxDelayMs === 'number' && node.data.checkboxDelayMs >= 0
        ? node.data.checkboxDelayMs
        : 500
    advanceAfter(node.id, ms, depth)
  }

  const actionHandlers: Record<string, Handler> = {
    'rotate-card': (node, depth) => {
      state.showBackFirst.value = !state.showBackFirst.value
      advanceNext(node.id, depth)
    },
    'set-initial-checkboxes': (node, depth) => {
      const checked =
        typeof node.data?.checkboxCount === 'number' && node.data.checkboxCount >= 0
          ? Math.floor(node.data.checkboxCount)
          : 0
      const groupId = state.activeGroupId.value ?? findNextGroupId(node.id) ?? '__all__'
      const cardIds = cardIdsForGroup(groupId)
      if (cardIds.length > 0) {
        const values = new Map(state.cardCheckboxValues.value)
        for (const cardId of cardIds) {
          const total = countCheckboxes(cardId)
          if (total > 0) values.set(cardId, initialCheckboxValues(total, checked))
        }
        state.cardCheckboxValues.value = values
      }
      advanceNext(node.id, depth)
    },
    'pull-next-card': pullNextCard,
    'pull-next-card-back-first': pullNextCard,
    delay: (node, depth) => {
      // Default to 1000 ms when unset (matches the editor default).
      const ms =
        typeof node.data?.delayMs === 'number' && node.data.delayMs >= 0 ? node.data.delayMs : 1000
      advanceAfter(node.id, ms, depth)
    },
    'wait-for-user-action': (node, depth) => {
      state.isInitializing.value = false
      // Without a card on screen (pool exhausted) there is nothing to wait for.
      if (!state.currentCardId.value) {
        advanceNext(node.id, depth)
        return
      }
      if (props.manualStep || dbgPaused.value) {
        state.needsUserInteraction.value = true
        state.manualContinuation.value = null // set by the card click
      }
      // Otherwise the cursor parks here; handleCardClick continues.
    },
    'play-audio': (node) => {
      const { index: audioIndex, waitForAudio } = decodeAudioAction(node.data)
      const next = nextOf(node.id, ['next'])
      const epoch = state.epoch.value
      const isCurrent = () => state.epoch.value === epoch
      // Wait a tick so a card pulled just before is in the DOM.
      void nextTick(() => {
        if (!isCurrent()) return
        playCardAudio({
          root: deps.playerEl.value,
          activeCardId: () => state.currentCardId.value,
          card: deps.displayedCard,
          cardChangeAnimation: deps.cardChangeAnimation(),
          index: audioIndex,
          waitForAudio,
          isCurrent,
          proceed: () => {
            if (!isCurrent() || !next) return
            goTo(next)
          },
        })
      })
    },
    'emit-complete': () => {
      state.complete.value = true
    },
    'ask-known': (node, depth) => {
      state.isInitializing.value = false
      const cardId = state.currentCardId.value
      if (!cardId) {
        // Pool exhausted: nothing to answer, continue without recording.
        const next = nextOf(node.id, ['known', 'unknown', 'next'])
        if (next) advance(next, depth)
        return
      }
      // The ✓ / ✗ buttons of the shell call answerCard().
      state.awaitingAnswer.value = { nodeId: node.id, cardId }
    },
    'offer-practice': (node, depth) => {
      const trackId = graphStr(node.data, 'trackId') ?? 'practice'
      const signal = `offer-practice:${trackId}`
      if (!state.flowSignals.value.includes(signal)) {
        state.flowSignals.value = [...state.flowSignals.value, signal]
      }
      advanceNext(node.id, depth)
    },
    'show-media': showMedia,
    'show-image': showMedia,
    'show-video': showMedia,
    'show-audio': showMedia,
    'show-pdf': showMedia,
    'click-checkbox': tickCheckboxes,
    'unclick-checkbox': tickCheckboxes,
    'phase-save': (node, depth) => {
      state.registers.value = EMPTY_REGISTERS()
      saveFlowProgress()
      if (!state.isInitializing.value) {
        enterInterlude(node.id, state.activePhaseId.value, true)
        return
      }
      advanceNext(node.id, depth)
    },
    'get-value': (node, depth) => {
      const register = graphStr(node.data, 'getRegister') ?? 'R1'
      const value = readGetterSource(
        graphStr(node.data, 'getSource') ?? 'literal',
        node.data?.getLit,
        {
          activeGroupId: state.activeGroupId.value,
          deckGroups: props.deckGroups ?? [],
          deckCardIds: deps.deckCardIds.value,
          pools: state.pools.value,
          doneCardIds: state.doneCardIds.value,
          checkboxValues: state.cardCheckboxValues.value,
          countCheckboxes,
          cardAnswers: state.cardAnswers.value,
          flowContext: props.flowContext,
        },
        graphStr(node.data, 'getPhase') ?? '',
      )
      state.registers.value = { ...state.registers.value, [register]: value }
      advanceNext(node.id, depth)
    },
    'set-value': (node, depth) => {
      const target = graphStr(node.data, 'setTarget') ?? 'progress-total'
      const value = readSetValueInput(
        graphStr(node.data, 'setInput') ?? 'R1',
        state.registers.value,
        node.data?.setLit,
      )
      if (target === 'progress-total') state.progressTotal.value = value
      else if (target === 'progress-current') state.progressCurrent.value = value
      advanceNext(node.id, depth)
    },
    'math-op': (node, depth) => {
      const target = graphStr(node.data, 'mathTarget') ?? 'R1'
      const result = applyMathOp(
        graphStr(node.data, 'mathOp') ?? '+1',
        state.registers.value[target] ?? 0,
        graphStr(node.data, 'mathOperand') ?? 'literal',
        state.registers.value,
        node.data?.mathLit,
      )
      state.registers.value = { ...state.registers.value, [target]: result }
      advanceNext(node.id, depth)
    },
  }

  /** Executes node `nodeId`; non-interactive nodes advance automatically. */
  function exec(nodeId: string, depth = 0): void {
    if (depth > 300) return
    const node = nodeById.value.get(nodeId)
    if (!node) return
    if (executePersonalReview({ node, depth, deps, props, state, start, nextOf, advanceNext })) return
    if (state.awaitingAnswer.value?.nodeId !== nodeId) state.awaitingAnswer.value = null
    // Own-property lookup: action types are author data (e.g. "constructor").
    if (node.type === 'action') {
      const actionType = graphStr(node.data, 'actionType') ?? ''
      if (hasOwn(actionHandlers, actionType)) actionHandlers[actionType]!(node, depth)
      else advanceNext(node.id, depth) // unknown action: skip
      return
    }
    if (hasOwn(structureHandlers, node.type)) structureHandlers[node.type]!(node, depth)
  }

  /** (Re)starts the flow at the start node, or at a saved phase checkpoint. */
  function start(resumePhaseIdOverride?: string) {
    resetForStart(state)
    if (cardClickTimer !== null) {
      clearTimeout(cardClickTimer)
      cardClickTimer = null
    }
    state.needsUserInteraction.value = false
    state.phaseInterlude.value = false
    state.phaseResume.value = null
    state.showingMedia.value = null
    state.showMediaResume.value = null
    state.cardTransition.value = 'none'
    state.callStack.value = []
    state.isInitializing.value = true

    // Seed persisted checkbox states so conditions work after a resume. Empty arrays come
    // from card-opened events without interaction; they would block the card-structure
    // fallback of click-checkbox, so only non-empty entries are kept.
    const initial = new Map<string, boolean[]>()
    for (const [cardId, values] of Object.entries(props.initialValues ?? {})) {
      if (Array.isArray(values) && values.length > 0) initial.set(cardId, values.map(Boolean))
    }
    state.cardCheckboxValues.value = initial
    initPools()

    const graph = deps.graph.value
    if (!graph) return

    const resumePhaseId = resumePhaseIdOverride ?? props.initialFlowPhaseId
    if (resumePhaseId) {
      const phaseNode = graph.nodes.find(
        (n) => n.type === 'phase' && graphStr(n.data, 'phaseId') === resumePhaseId,
      )
      if (phaseNode) {
        state.cursorNodeId.value = phaseNode.id
        exec(phaseNode.id) // the flow clears isInitializing once it reaches a stable state
        return
      }
    }

    const startNode =
      graph.nodes.find((n) => n.type === 'start') ?? graph.nodes.find((n) => n.id === 'node-start')
    if (!startNode) {
      state.isInitializing.value = false
      return
    }
    state.cursorNodeId.value = startNode.id
    if (props.manualStep || dbgPaused.value) {
      // Park at the start node; the first step continues.
      const next = nextOf(startNode.id, ['next'])
      if (next) state.manualContinuation.value = () => goTo(next)
      state.isInitializing.value = false
      return
    }
    exec(startNode.id)
  }

  // Learner input
  let cardClickTimer: ReturnType<typeof setTimeout> | null = null

  const atWaitNode = () => {
    const nodeId = state.cursorNodeId.value
    const node = nodeId ? nodeById.value.get(nodeId) : undefined
    return node?.type === 'action' && graphStr(node.data, 'actionType') === 'wait-for-user-action'
      ? node
      : null
  }

  /** A card click resolves a wait-for-user-action node (after its optional delay). */
  const handleCardClick = () => {
    if (props.model !== 'flowchart') return
    const node = atWaitNode()
    if (props.manualStep || dbgPaused.value) {
      if (!node || !state.needsUserInteraction.value) return
      state.needsUserInteraction.value = false
      const next = nextOf(node.id, ['next'])
      if (next) state.manualContinuation.value = () => goTo(next)
      return
    }
    if (!node) return
    const next = nextOf(node.id, ['next'])
    if (!next) return
    const ms =
      typeof node.data?.waitDelayMs === 'number' && node.data.waitDelayMs >= 0
        ? node.data.waitDelayMs
        : 0
    if (ms === 0) {
      state.cursorNodeId.value = next
      exec(next)
      return
    }
    if (cardClickTimer !== null) clearTimeout(cardClickTimer)
    cardClickTimer = window.setTimeout(() => {
      cardClickTimer = null
      state.cursorNodeId.value = next
      exec(next)
    }, ms)
  }

  /** ✓ / ✗ answer for the card an "Ask: known?" node is waiting on. */
  const answerCard = (known: boolean) => {
    const waiting = state.awaitingAnswer.value
    if (!waiting) return
    state.awaitingAnswer.value = null
    state.cardAnswers.value = new Map(state.cardAnswers.value).set(waiting.cardId, known)
    const next = nextOf(waiting.nodeId, [known ? 'known' : 'unknown'], true)
    if (!next) return
    if (props.manualStep || dbgPaused.value) {
      state.manualContinuation.value = () => goTo(next)
      return
    }
    goTo(next)
  }

  /** Checkbox clicks are tracked per card and saved so they survive reloads. */
  const handleFlowInteraction = (interaction: DeckElementInteraction) => {
    if (interaction.elementType !== 'checkbox-group') return
    const cardId = state.currentCardId.value
    if (!cardId) return
    const detail = interaction.detail as { values?: unknown }
    const values = Array.isArray(detail?.values) ? detail.values.map(Boolean) : []
    state.cardCheckboxValues.value = new Map(state.cardCheckboxValues.value).set(cardId, values)
    saveFlowProgress() // no-op without an active phase
  }

  const dismissShowMedia = () => {
    state.showMediaResume.value?.()
  }

  /** Advances one step in manual mode (editor preview or paused debugging). */
  const stepForward = () => {
    if (!props.manualStep && !dbgPaused.value) return
    if (state.needsUserInteraction.value) return
    // The editor never wires the host's media Continue button, so skip media there.
    if (props.inEditor && state.showingMedia.value) {
      state.showMediaResume.value?.()
      return
    }
    const continuation = state.manualContinuation.value
    if (!continuation) return
    state.manualContinuation.value = null
    continuation()
  }

  /** Continuation to the node after the cursor (conditions try true, then else). */
  const followFromCursor = () => {
    const nodeId = state.cursorNodeId.value
    const node = nodeId ? nodeById.value.get(nodeId) : null
    if (!node) return null
    const ports =
      node.type === 'condition'
        ? ['true', 'else']
        : node.type === 'end'
          ? []
          : node.type === 'action'
            ? (actionOutputPorts(node.data) ?? ['next'])
            : ['next']
    for (const port of ports) {
      const next = nextOf(nodeId!, [port])
      if (next) return next
    }
    return null
  }

  const resumeInterlude = () => {
    const resume = state.phaseResume.value
    state.phaseResume.value = null
    state.phaseInterlude.value = false
    resume?.()
  }

  const dbgPause = () => {
    dbgPaused.value = true
    state.epoch.value += 1 // cancel in-flight delay timers
    // Without a stored continuation (synchronous flow), synthesize one so Step works.
    // Interludes and media nodes are blocked on their own Continue: inventing a
    // continuation there would skip them and, on Resume, could cascade to the end.
    if (state.manualContinuation.value || state.phaseInterlude.value || state.showingMedia.value)
      return
    if (atWaitNode()) {
      state.needsUserInteraction.value = true // the card click sets the continuation
      return
    }
    const next = followFromCursor()
    if (next) state.manualContinuation.value = () => goTo(next)
  }

  const dbgResume = () => {
    dbgPaused.value = false
    // Blocked on a wait node: the card stays and a normal click continues.
    if (state.needsUserInteraction.value) {
      state.needsUserInteraction.value = false
      state.manualContinuation.value = null
      return
    }
    const continuation = state.manualContinuation.value
    if (continuation) {
      state.manualContinuation.value = null
      continuation()
    }
    // A phase interlude stays on screen until its Continue is clicked.
  }

  const dbgStep = () => {
    if (!dbgPaused.value) return
    if (state.phaseInterlude.value && state.phaseResume.value) {
      resumeInterlude()
      return
    }
    const continuation = state.manualContinuation.value
    if (continuation) {
      state.manualContinuation.value = null
      state.needsUserInteraction.value = false
      state.epoch.value += 1 // cancel a pending auto-play timer
      continuation()
      return
    }
    if (state.showingMedia.value) return // media waits for its own Continue
    // Fallback: advance from the cursor (also past wait-for-user-action).
    if (!state.cursorNodeId.value || !nodeById.value.get(state.cursorNodeId.value)) return
    state.needsUserInteraction.value = false
    state.epoch.value += 1
    const next = followFromCursor()
    if (next) goTo(next)
  }

  /**
   * Skip button: force exactly one step, past media, interludes and wait nodes. Media
   * is resumed directly; announcing it would only show the host's own Continue.
   */
  const skipToNextNode = () => {
    if (state.showingMedia.value) {
      // eslint-disable-next-line no-console
      console.warn('[PFC-DBG] Skipknopf force-skipping media', {
        build: DEBUG_BUILD_TAG,
        mediaId: state.showingMedia.value.mediaId,
        hasResumeFn: typeof state.showMediaResume.value === 'function',
      })
      state.showMediaResume.value?.()
      return
    }
    state.epoch.value += 1 // cancel a pending auto-play/delay timer
    if (state.phaseInterlude.value && state.phaseResume.value) {
      resumeInterlude()
      return
    }
    const continuation = state.manualContinuation.value
    if (continuation) {
      state.manualContinuation.value = null
      state.needsUserInteraction.value = false
      continuation()
      return
    }
    if (!state.cursorNodeId.value || !nodeById.value.get(state.cursorNodeId.value)) return
    state.needsUserInteraction.value = false
    const next = followFromCursor()
    if (next) goTo(next)
  }

  /** All getter sources and setter targets, for the debug panel and editor preview. */
  const debugState = computed(() => {
    const groupId = state.activeGroupId.value
    const allIds = deps.deckCardIds.value
    const done = state.doneCardIds.value
    const group =
      groupId && groupId !== '__all__'
        ? ((props.deckGroups ?? []).find(
            (g) => typeof g?.id === 'string' && g.id.trim() === groupId,
          ) ?? null)
        : null
    return {
      deckSize: allIds.length,
      deckDone: done.size,
      deckPoolRemaining: allIds.filter((id) => !done.has(id)).length,
      groupSize: groupId === '__all__' ? allIds.length : group ? group.cardIds.length : 0,
      groupPoolRemaining: groupId ? (state.pools.value[groupId]?.length ?? 0) : 0,
      groupDone:
        groupId === '__all__'
          ? done.size
          : group
            ? group.cardIds.filter((id) => done.has(id)).length
            : 0,
      // null = automatic count
      progressTotal: state.progressTotal.value,
      progressCurrent: state.progressCurrent.value,
    }
  })

  // Watchers (registration order matches the original component)
  watch(
    () => props.deckId,
    () => resetForDeckChange(state),
  )

  watch(
    () => [state.cursorNodeId.value, state.needsUserInteraction.value] as const,
    ([nodeId]) => {
      if (props.model !== 'flowchart') return
      emit('flowchart-step-changed', {
        nodeId,
        needsUserInteraction: state.needsUserInteraction.value,
      })
    },
  )

  watch(
    () => props.stepSignal,
    (newValue, oldValue) => {
      if (!props.manualStep || newValue === oldValue) return
      stepForward()
    },
  )

  // Switching from manual to automatic (Continue/Play after a pause) resumes the flow.
  watch(
    () => props.manualStep,
    (isManual, wasManual) => {
      if (isManual || !wasManual) return
      const continuation = state.manualContinuation.value
      if (continuation && !state.needsUserInteraction.value) {
        state.manualContinuation.value = null
        continuation()
      }
    },
  )

  return {
    dbgPaused,
    dbgStepDelayMs,
    nodeById,
    countCheckboxes,
    start,
    stepForward,
    handleCardClick,
    handleFlowInteraction,
    answerCard,
    dismissShowMedia,
    dbgPause,
    dbgResume,
    dbgStep,
    skipToNextNode,
    resumeInterlude,
    debugState,
  }
}

/**
 * Marker logged by debug tooling; bump it when debug logging changes so a stale
 * cached bundle is easy to recognise in the console.
 */
export const DEBUG_BUILD_TAG = 'skipbtn-drag-simple-r5'

export type FlowchartRunner = ReturnType<typeof useFlowchartRunner>
